import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Button, Input, Form, InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import './Login.scss';
import NeedHelp from 'components/needHelp/NeedHelp';
import { maskPhoneNumber, showErrorNotification } from 'utils/commonUtils';
import {
  buildEmailInputPath,
  buildPasswordCreatePath,
  buildPasswordInputPath,
  buildRegistrationPath,
} from 'utils/pathBuilder';
import {
  checkUserStatus,
  requestOTP,
  loginWithOTP,
  // updatedTnc,
} from 'services/loginService';
import { useSession } from 'context/SessionContext';
import CarouselContainer from 'components/carouselContainer/CarouselContainer';
import TermsAndConditions from 'components/termsAndConditions/TermsAndConditions';
import { SessionDTO } from 'types/dto/login';
import { fetchLatestTermsAndConditions } from 'services/termsService';

type LoginStep = 'input-phone' | 'otp-verification';

// Maximum number of incorrect OTP entries before the token is invalidated
// and the user must request a new one (brute-force protection).
const MAX_OTP_ATTEMPTS = 5;

interface PhoneFormData {
  countryCode: string;
  phoneNumber: string;
}

interface VerificationFormData {
  verificationCode: string;
}

const Login: React.FC = () => {
  const { t, i18n } = useTranslation('login');
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const [phoneForm] = Form.useForm<PhoneFormData>();
  const [verificationForm] = Form.useForm<VerificationFormData>();
  const [currentStep, setCurrentStep] = useState<LoginStep>('input-phone');
  const [isExiting, setIsExiting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpAttemptCount, setOtpAttemptCount] = useState(0);
  const [otpLocked, setOtpLocked] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const { session, setSession } = useSession();

  const [termsVisible, setTermsVisible] = React.useState(false);
  const [termsData, setTermsData] = useState<any>(null);
  const [tempUserStatus, setTempUserStatus] =
    useState<Partial<SessionDTO> | null>(null);
  const verificationInputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (currentStep === 'otp-verification' && !isExiting) {
      const timer = setTimeout(() => {
        if (verificationInputRef.current) {
          verificationInputRef.current.input?.focus();
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isExiting]);

  const handleCloseTerms = () => {
    setTermsVisible(false);
  };

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Re-validate form fields on language change so error messages update
  useEffect(() => {
    const phoneErrors = phoneForm.getFieldsError(['phoneNumber']);
    if (phoneErrors.some((field) => field.errors.length > 0)) {
      phoneForm.validateFields(['phoneNumber']);
    }
    const verificationErrors = verificationForm.getFieldsError([
      'verificationCode',
    ]);
    if (verificationErrors.some((field) => field.errors.length > 0)) {
      verificationForm.validateFields(['verificationCode']);
    }
  }, [i18n.language, phoneForm, verificationForm]);

  const maskedPhoneNumber = useMemo(
    () => maskPhoneNumber(phoneNumber),
    [phoneNumber],
  );

  const goToNextStepBySession = async (currentSession: SessionDTO | null, isTermsAccepted = false) => {

    const phone = phoneNumber;

    if (currentSession?._st === 'new') {
      navigate(buildRegistrationPath(), {
        state: {
          phoneNumber: phone,
          session: currentSession,
        },
      });
    } else if (currentSession?._st === 'not_found') {
      navigate(buildRegistrationPath(), {
        state: {
          phoneNumber: phone,
          session: currentSession,
        },
      });
    } else if (currentSession?._st === 'activated') {
      let sessionToUse = { ...currentSession };
      const navOptions = {
        state: {
          phoneNumber: phone,
          session: currentSession,
        },
      };
      if(!isTermsAccepted && currentSession._ve === false && currentSession._vp === false){
        try {
          const response = await fetchLatestTermsAndConditions();

          if (response && response.data) {
            setTermsData(response.data);
            setTermsVisible(true);

            sessionToUse = {
              ...sessionToUse,
              _tc: true
            };
            setSession(sessionToUse);

            return;
          }
        } catch (error) {
          console.error('Fetch T&C failed', error);
        }
      }
      if (currentSession._ve === false) {
        navigate(buildEmailInputPath(currentSession), navOptions);
      } else if (currentSession._vp === false) {
        navigate(buildPasswordCreatePath(), navOptions);
      } else {
        navigate(buildPasswordInputPath(), navOptions);
      }
    }
  };

  // Handle step transition with animation
  const transitionToStep = useCallback((nextStep: LoginStep) => {
    setIsExiting(true);
    setTimeout(() => {
      setCurrentStep(nextStep);
      setIsExiting(false);
    }, 400); // Match fade-out animation duration
  }, []);

  // Step 1: Request verification code
  const handleGetVerificationCode = async () => {
    try {
      await phoneForm.validateFields(['phoneNumber']);
      const values = phoneForm.getFieldsValue();

      const countryCode = '+852';
      const currentPhone = values.phoneNumber;
      setPhoneNumber(currentPhone);

      try {
        setLoading(true);
        const statusResponse = await checkUserStatus(countryCode, currentPhone);
        setTempUserStatus({
          _st: statusResponse.status, // status -> _st
          _vp: statusResponse.is_password_created, // is_password_created -> _vp
          _ve: statusResponse.is_email_verified, // is_email_verified -> _ve
        });

        // API Request OTP
        await requestOTP(currentPhone);
        setOtpAttemptCount(0);
        setOtpLocked(false);

        // Start countdown
        setCountdown(15 * 60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        transitionToStep('otp-verification');
      } catch (error) {
        showErrorNotification(t('getVerificationCodeFailed'), error as Error);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const localizeBackendError = (details: string): string => {
    const normalized = details.toLowerCase();
    if (normalized.includes('account locked')) {
      return t('accountLocked');
    }
    if (normalized.includes('maximum otp attempt')) {
      return t('otpAttemptsExceeded');
    }
    return t('otpIncorrect');
  };

  // Step 2: Verify sesssion by email and password
  const handleLogin = async () => {
    try {
      const values = await verificationForm.validateFields([
        'verificationCode',
      ]);

      // OTP has been invalidated after too many incorrect attempts:
      // block verification until the user requests a new code.
      if (otpLocked) {
        showErrorNotification(t('otpAttemptsExceeded'));
        return;
      }

      try {
        setLoading(true);
        const tokenResponse = await loginWithOTP(
          phoneNumber,
          values.verificationCode,
        );
        const countryCode = '+852';
        const statusResponse = await checkUserStatus(countryCode, phoneNumber);
        const finalSession: SessionDTO = {
          _tk: tokenResponse?.access_token, // session ID is access_token
          _rt: tokenResponse?.refresh_token, //oauth 2 response
          // _mt: tokenResponse, //oauth 2 response
          _lg: tempUserStatus?._lg,
          _pu: tempUserStatus?._pu,
          _st: statusResponse?.status || 'new',
          _ve: statusResponse?.is_email_verified || false,
          _vp: statusResponse?.is_password_created || false,
          // _ph: phoneNumber,
          _at: 'otp',
          // _id: tempUserStatus?._id,
          // _n1: tempUserStatus?._n1,
          // _n2: tempUserStatus?._n2,Header
          // _tp: values.verificationCode
        };

        setSession(finalSession);

        // Successful login: clear the incorrect-attempt counter.
        setOtpAttemptCount(0);

        if (finalSession._tc) {
          setTermsVisible(true);
        } else {
          goToNextStepBySession(finalSession);
        }
      } catch (error) {
        const backendDetails = (error as any)?.response?.data?.details;
        if (backendDetails) {
          // Backend reports the OTP is used up: lock it so the user must
          // request a new code (disable confirm, enable resend).
          if (backendDetails.toLowerCase().includes('maximum otp attempt')) {
            setOtpLocked(true);
            setCountdown(0);
          }
          showErrorNotification(localizeBackendError(backendDetails));
          return;
        }

        // Incorrect OTP: count the attempt. After the max, invalidate the
        // token and force the user to request a new OTP.
        const nextAttempt = otpAttemptCount + 1;
        setOtpAttemptCount(nextAttempt);

        if (nextAttempt >= MAX_OTP_ATTEMPTS) {
          setOtpLocked(true);
          setCountdown(0); // allow the user to resend immediately
          showErrorNotification(t('otpAttemptsExceeded'));
        } else {
          showErrorNotification(t('otpIncorrect'));
        }
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    // Resend is only allowed after the OTP has been locked by too many
    // incorrect entries; otherwise the button stays disabled.
    if (!otpLocked) return;

    try {
      await requestOTP(phoneNumber);

      // New OTP issued: clear the incorrect-attempt lock and counter.
      setOtpAttemptCount(0);
      setOtpLocked(false);
      verificationForm.resetFields(['verificationCode']);

      // Restart countdown
      setCountdown(15 * 60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      showErrorNotification(t('getVerificationCodeFailed'), error as Error);
    }
  };

  // Render Step 1: Phone Number Input
  const renderPhoneStep = () => (
    <div className={`login-form-section ${isExiting ? 'exiting' : 'visible'}`}>
      <div className="login-title">{t('title')}</div>

      <Form
        form={phoneForm}
        layout="vertical"
        className="login-form"
        validateTrigger={[]}
        onFinish={handleGetVerificationCode}
      >
        <Form.Item
          name="phoneNumber"
          label={t('mobileNumber')}
          // required={false}
          rules={[
            {
              required: true,
              message: t('phoneRequired'),
            },
            {
              pattern: /^\d{8}$/,
              message: t('phoneInvalid'),
            },
          ]}
          // hasFeedback
        >
          <Input
            type="tel"
            //addonBefore="+852"
            prefix="+852"
            placeholder={t('phonePlaceholder')}
            maxLength={8}
            className="phone-input"
            onChange={(e) => {
              const value = e.target.value;
              if (value.length === 8) {
                phoneForm.validateFields(['phoneNumber']);
              }
            }}
          />
        </Form.Item>

        <Button
          type="primary"
          block
          htmlType="submit"
          loading={loading}
          className="submit-button"
        >
          {t('getVerificationCode')}
        </Button>
      </Form>

      <NeedHelp />
    </div>
  );

  // Render Step 2: Verification Code Input
  const renderVerificationStep = () => (
    <div className={`login-form-section ${isExiting ? 'exiting' : 'visible'}`}>
      <div className="login-title">{t('title')}</div>
      <div className="verification-subtitle">
        {t('codeSentTo')} {maskedPhoneNumber}
      </div>

      <Form
        form={verificationForm}
        layout="vertical"
        className="login-form"
        onFinish={handleLogin}
        validateTrigger={[]}
      >
        <Form.Item
          name="verificationCode"
          label={t('verificationCode')}
          // required={false}
          rules={[
            {
              required: true,
              message: t('codeRequired'),
            },
            {
              pattern: /^\d{6}$/,
              message: t('codeInvalid'),
            },
          ]}
          // hasFeedback
        >
          <Input
            ref={verificationInputRef}
            type="tel"
            placeholder={t('verificationCodePlaceholder')}
            maxLength={6}
            className="verification-input"
            onChange={(e) => {
              const value = e.target.value;
              if (value.length === 6) {
                verificationForm.validateFields(['verificationCode']);
              }
            }}
          />
        </Form.Item>

        <Button
          type="primary"
          block
          htmlType="submit"
          loading={loading}
          disabled={otpLocked}
          className="submit-button"
        >
          {t('confirm')}
        </Button>

        <Button
          type="default"
          block
          onClick={handleResendCode}
          disabled={!otpLocked}
          color="primary"
          variant="outlined"
          className="resend-button"
        >
          {countdown > 0
            ? t('resendIn', {
                time: `${Math.floor(countdown / 60)}:${String(
                  countdown % 60,
                ).padStart(2, '0')}`,
              })
            : t('resendCode')}
        </Button>
      </Form>

      <NeedHelp />
    </div>
  );

  return (
    <>
      <CarouselContainer>
        <div className="login-form-container">
          {currentStep === 'input-phone' && renderPhoneStep()}
          {currentStep === 'otp-verification' && renderVerificationStep()}
        </div>
      </CarouselContainer>
      <TermsAndConditions
        visible={termsVisible}
        onOK={async () => {
          if (session) {
            try {
              // hotfix: temporarily disable TnC update API call to avoid 403 error
              // await updatedTnc("","");
              // const updatedSession = {
              //   ...session,
              //   needTnC: false,
              // };
              // setSession(updatedSession);
              setTermsVisible(false);
              goToNextStepBySession(session, true);
            } catch (error) {
              console.error('Failed to update TnC', error);
            }
          }
        }}
        onClose={handleCloseTerms}
        data={termsData}
      />
    </>
  );
};

export default Login;
